import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField as MuiTextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import ColorPicker from "../../shared/ColorPicker/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

interface FeeRow {
  title: string;
  amount: string;
}

const emptyFeeRow = (): FeeRow => ({ title: "", amount: "" });

export default function FeeStructureCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      icon: "",
      color_code: "#667eea",
      fee_structure: [emptyFeeRow()],
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      fee_structure: Yup.array().of(
        Yup.object({
          title: Yup.string().required("Title is required"),
          amount: Yup.number()
            .typeError("Amount must be a number")
            .min(0, "Amount must be at least 0")
            .required("Amount is required"),
        }),
      ),
    }),
    onSubmit: async (values) => {
      const payload = {
        title: values.title,
        icon: values.icon,
        color_code: values.color_code,
        fee_structure: values.fee_structure.map((row) => ({
          title: row.title,
          amount: Number(row.amount) || 0,
        })),
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("fee-structures/" + id, payload);
        } else {
          response = await request.post("fee-structures/create", payload);
        }

        if (response?.data?.status) {
          toast.success(
            isEdit
              ? "Fee structure updated successfully!"
              : "Fee structure created successfully!",
          );
          navigate("/admin/fee-structure");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  const handleAddRow = () => {
    formik.setFieldValue("fee_structure", [
      ...formik.values.fee_structure,
      emptyFeeRow(),
    ]);
  };

  const handleRemoveRow = (index: number) => {
    const rows = [...formik.values.fee_structure];
    rows.splice(index, 1);
    formik.setFieldValue("fee_structure", rows.length ? rows : [emptyFeeRow()]);
  };

  const handleRowChange = (index: number, field: string, value: string) => {
    const rows = [...formik.values.fee_structure];
    rows[index] = { ...rows[index], [field]: value };
    formik.setFieldValue("fee_structure", rows);
  };

  useEffect(() => {
    if (isEdit) {
      const fetchFeeStructure = async () => {
        setLoading(true);
        try {
          const response = await request.get(`fee-structures/${id}`);
          const data = response.data.data;
          const feeRows = Array.isArray(data.fee_structure)
            ? data.fee_structure.map((r) => ({
                title: r?.title ?? "",
                amount: r?.amount != null ? String(r.amount) : "",
              }))
            : [emptyFeeRow()];

          formik.setValues({
            title: data.title || "",
            icon: data.icon || "",
            color_code: data.color_code || "#667eea",
            fee_structure: feeRows,
            sort_order: data.sort_order?.toString() || "",
            isActive: data.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchFeeStructure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const feeErrors = (formik.errors as any)?.fee_structure as
    | Array<{ title?: string; amount?: string } | undefined>
    | undefined;

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Fee Structure" : "Create New Fee Structure"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/fee-structure"),
              }}
              primaryButton={{
                label: isEdit ? "Update Fee Structure" : "Create Fee Structure",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="title"
                    label="Title"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="icon"
                    label="Icon"
                    type="text"
                    formik={formik}
                    placeholder="e.g. payments"
                  />
                </Box>
              </Box>

              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <ColorPicker
                    name="color_code"
                    formik={formik}
                    placeholder="#667eea"
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box sx={{ marginTop: "24px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#1a1f3a" }}>
                    Fee Structure
                  </Typography>
                  <IconButton
                    onClick={handleAddRow}
                    size="small"
                    sx={{
                      color: "#667eea",
                      border: "1px solid #667eea",
                      borderRadius: "8px",
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {formik.values.fee_structure.map((row, index) => {
                  const rowError = feeErrors?.[index];
                  return (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <MuiTextField
                            fullWidth
                            size="small"
                            label="Title"
                            value={row.title}
                            onChange={(e) =>
                              handleRowChange(index, "title", e.target.value)
                            }
                            error={Boolean(rowError?.title)}
                            sx={{ mb: 0 }}
                          />
                          {rowError?.title && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#d32f2f",
                                mt: 0.5,
                              }}
                            >
                              {rowError.title}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <MuiTextField
                            fullWidth
                            size="small"
                            label="Amount"
                            type="number"
                            value={row.amount}
                            onChange={(e) =>
                              handleRowChange(index, "amount", e.target.value)
                            }
                            error={Boolean(rowError?.amount)}
                            sx={{ mb: 0 }}
                          />
                          {rowError?.amount && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#d32f2f",
                                mt: 0.5,
                              }}
                            >
                              {rowError.amount}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          onClick={() => handleRemoveRow(index)}
                          size="small"
                          sx={{
                            color: "#ef4444",
                            alignSelf: "center",
                          }}
                          title="Remove row"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
